import React, { useState } from 'react';
import { Camera, User, BadgeCheck, ShieldCheck, ChevronRight } from 'lucide-react';
import { db } from '../../lib/firebase';
import { doc, writeBatch } from 'firebase/firestore';
import { handleFirestoreError } from '../../lib/errorHandlers';
import { UserProfile, Player } from '../../types';
import { motion, AnimatePresence } from 'motion/react';
import { signOut } from 'firebase/auth';
import { auth } from '../../lib/firebase';

interface ProfileSetupModalProps {
  user: { uid: string; email: string | null; displayName: string | null };
  onComplete: (profile: UserProfile) => void;
  onCancel?: () => void;
  initialPlayer?: Player;
  logoUrl?: string;
  bgUrl?: string;
  bgBlur?: boolean;
}

export function ProfileSetupModal({ user, onComplete, onCancel, initialPlayer, bgUrl, bgBlur }: ProfileSetupModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [name, setName] = useState(initialPlayer?.name?.split(' ')[0] || user.displayName?.split(' ')[0] || '');
  const [surname, setSurname] = useState(initialPlayer?.name?.split(' ').slice(1).join(' ') || user.displayName?.split(' ').slice(1).join(' ') || '');
  const [gender, setGender] = useState<'Masculino' | 'Femenino' | null>((initialPlayer?.gender as any) || null);
  const [category, setCategory] = useState(initialPlayer?.category || '');

  React.useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name || !surname || !gender || !category) {
      setError('Por favor completa todos los campos.');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const now = Date.now();
      const fullName = `${name} ${surname}`.trim();

      const profile: UserProfile = {
        uid: user.uid,
        email: user.email || '',
        name: fullName,
        role: 'user',
        photoUrl: '',
        phone: '', // Phone is collected later when booking
        location: 'RN', // Default
        city: 'general_roca', // Default
        isProfileComplete: true,
        acceptedTermsAt: now,
        createdAt: now,
        notificationsEnabled: false,
        fcmTokens: []
      };

      const player: Player = {
        id: user.uid,
        name: fullName,
        nickname: '',
        category: category,
        photoUrl: '',
        phone: '',
        city: 'general_roca',
        gender: gender,
        isCategoryLocked: false,
        isValidated: false,
        createdAt: now,
      };

      const batch = writeBatch(db);
      batch.set(doc(db, 'users', user.uid), profile, { merge: true });
      batch.set(doc(db, 'players', user.uid), player);
      await batch.commit();

      onComplete(profile);
    } catch (e: any) {
      console.error(e);
      handleFirestoreError(e, 'write', 'users/players');
      setError('Ocurrió un error al guardar tu perfil.');
    } finally {
      setLoading(false);
    }
  };

  const categories = ['8va', '7ma', '6ta', '5ta', '4ta', '3ra', '2da', '1ra'];

  return (
    <div className="fixed inset-0 z-[200] flex flex-col items-center justify-center p-4 py-8 overflow-y-auto">
      {/* Background */}
      <div className="absolute inset-0 z-0 bg-[#030712] overflow-hidden">
        {bgUrl && (
          <div
            className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-700 ${bgBlur ? 'blur-sm scale-105' : ''}`}
            style={{ backgroundImage: `url(${bgUrl})` }}
          />
        )}
        <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-md" />
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="relative z-10 w-full max-w-sm bg-gradient-to-b from-white/[0.08] via-slate-950/[0.45] to-slate-950/[0.7] backdrop-blur-3xl border border-white/[0.12] rounded-[2rem] shadow-[0_30px_75px_rgba(0,0,0,0.65)] overflow-hidden"
      >
        <div className="p-6 text-center">
          <div className="h-12 w-12 mx-auto mb-3 rounded-full bg-[var(--primary-color)]/10 flex items-center justify-center border border-[var(--primary-color)]/20">
            <User size={24} className="text-[var(--primary-color)]" />
          </div>
          <h1 className="text-xl font-black uppercase italic text-white tracking-tight leading-none mb-1">
            Configura tu Perfil
          </h1>
          <p className="text-[10px] text-white/50 font-medium">Solo necesitamos unos datos básicos para empezar</p>
        </div>

        {error && (
          <div className="px-6 pb-2">
            <div className="w-full bg-red-500/10 border border-red-500/20 text-red-400 text-[10px] font-bold text-center py-2 rounded-xl">
              {error}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-[9px] font-black uppercase tracking-wider text-slate-200 ml-1">Nombre</label>
              <input
                type="text"
                required
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Tu nombre"
                className="w-full bg-slate-950/40 border border-white/10 rounded-xl px-4 py-3 outline-none font-bold text-xs text-white focus:border-[var(--primary-color)]/40 transition-all"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[9px] font-black uppercase tracking-wider text-slate-200 ml-1">Apellido</label>
              <input
                type="text"
                required
                value={surname}
                onChange={e => setSurname(e.target.value)}
                placeholder="Tu apellido"
                className="w-full bg-slate-950/40 border border-white/10 rounded-xl px-4 py-3 outline-none font-bold text-xs text-white focus:border-[var(--primary-color)]/40 transition-all"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[9px] font-black uppercase tracking-wider text-slate-200 ml-1">Género</label>
            <div className="grid grid-cols-2 gap-2">
              {(['Masculino', 'Femenino'] as const).map(g => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGender(g)}
                  className={`py-3 rounded-xl text-[10px] font-black uppercase transition-all border ${
                    gender === g 
                      ? 'bg-[var(--primary-color)] border-[var(--primary-color)] text-black' 
                      : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[9px] font-black uppercase tracking-wider text-slate-200 ml-1">Tu Categoría Actual</label>
            <select
              required
              value={category}
              onChange={e => setCategory(e.target.value)}
              className="w-full bg-slate-950/40 border border-white/10 rounded-xl px-4 py-3 outline-none font-bold text-xs text-white focus:border-[var(--primary-color)]/40 transition-all appearance-none"
            >
              <option value="" disabled className="bg-slate-900 text-white/50">Selecciona tu categoría</option>
              {categories.map(c => (
                <option key={c} value={c} className="bg-slate-900 text-white">{c}</option>
              ))}
            </select>
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[var(--primary-color)] hover:bg-[var(--primary-color)]/90 text-black font-black py-4 rounded-xl uppercase tracking-widest text-[10px] shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50"
            >
              {loading ? 'Guardando...' : 'Comenzar a Jugar'}
            </button>
            <p className="text-[8px] text-white/30 text-center mt-3">
              Al continuar, aceptas nuestros términos y condiciones y confirmas que eres mayor de edad o tienes permiso de tus tutores.
            </p>
          </div>
        </form>
      </motion.div>

      <div className="mt-6 text-center relative z-10">
        <button
          onClick={async () => {
            await signOut(auth);
            window.location.replace('/?login=true');
          }}
          className="text-white/40 hover:text-white/70 text-xs font-medium transition-colors cursor-pointer"
        >
          Cerrar sesión
        </button>
      </div>
    </div>
  );
}
